import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Badge,
  Card,
  ConfigProvider,
  Flex,
  Typography,
  Select,
  Tooltip,
  Tag,
  Row,
  Col,
  Button,
  Skeleton,
  Popover,
} from 'antd';
import enUS from 'antd/lib/locale/en_US';
import deDE from 'antd/lib/locale/de_DE';
import ptBR from 'antd/lib/locale/pt_BR';
import {
  Asset as AssetType,
  AssetType as AssetTypeEnum,
  Video,
  VideoStatus,
} from '@monorepo/types';
import { useStore } from '../../helpers/use-store';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { LoadingState, useLoading } from '@monorepo/react-components';
import { getStatusColor } from '../series/series-videos/series-videos';
import { upperFirst } from 'lodash';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { Helmet } from 'react-helmet';
import { useResponsiveReady } from '../../hooks/use-responive-ready';
import AssetComponent from '../../components/asset/asset';
import SocialIcon from '../../components/social-provider-icon/social-provider-icon';
import { SocialProvider } from '@monorepo/types';
import { getVideoDisplayDate } from './utils/date-utils';
import { getStatusColors } from './utils/color-utils';
import WeeklyTimelineView from './components/weekly-timeline-view';
import DailyTimelineView from './components/daily-timeline-view';

import './videos-calendar.scss';

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

type CalendarMode = 'month' | 'year';
type ViewMode = 'daily' | 'weekly' | 'monthly';

const VideosCalendar = () => {
  const { t, i18n } = useTranslation();
  const antdLocale =
    i18n.language === 'de' ? deDE : i18n.language === 'pt-BR' ? ptBR : enUS;
  const navigate = useNavigate();
  const { loadingState, updateLoadingState } = useLoading(LoadingState.Loading);
  const [videos, setVideos] = useState<Video[]>([]);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [calendarMode, setCalendarMode] = useState<CalendarMode>('month');
  const [viewMode, setViewMode] = useState<ViewMode>('weekly');
  const {
    dataStore: { videoStore },
  } = useStore();
  const { isMobile } = useResponsiveReady();

  useEffect(() => {
    fetchVideos();
  }, [selectedDate, calendarMode]);

  const fetchVideos = async () => {
    try {
      updateLoadingState(LoadingState.Loading);

      const { startDate, endDate } = getDateRange();

      const videosResult = await videoStore.getVideosByDateRange(
        startDate.toISOString(),
        endDate.toISOString()
      );
      setVideos(videosResult);
    } catch (e) {
      console.error('Failed fetching videos for calendar', e);
    } finally {
      updateLoadingState(LoadingState.Loaded);
    }
  };

  const getDateRange = () => {
    let startDate: Dayjs;
    let endDate: Dayjs;

    if (calendarMode === 'month') {
      startDate = selectedDate.startOf('month');
      endDate = selectedDate.endOf('month');
    } else {
      startDate = selectedDate.startOf('year');
      endDate = selectedDate.endOf('year');
    }

    return { startDate, endDate };
  };

  const getVideosForDate = (date: Dayjs): Video[] => {
    return videos.filter((video) => {
      const videoDate = dayjs(getVideoDisplayDate(video));
      return videoDate.isSame(date, 'day');
    });
  };

  const getVideosForWeek = (date: Dayjs): Video[] => {
    const weekStart = date.startOf('week');
    const weekEnd = date.endOf('week');

    return videos.filter((video) => {
      const videoDate = dayjs(getVideoDisplayDate(video));
      return videoDate.isBetween(weekStart, weekEnd, 'day', '[]');
    });
  };

  const dateCellRender = (value: Dayjs) => {
    const dayVideos = getVideosForDate(value);

    if (dayVideos.length === 0) return null;

    return (
      <div className="calendar-cell-videos-list">
        {dayVideos.map((video) => {
          const videoWithAsset = video as Video & { firstAsset: AssetType };
          const { bgColor, borderColor } = getStatusColors(video.status);

          return (
            <div
              key={video._id}
              className="calendar-video-card"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/videos/${video._id}`);
              }}
              style={{
                background: bgColor,
                borderLeft: `3px solid ${borderColor}`,
              }}
            >
              <div className="calendar-video-compact-header">
                <Typography.Text className="calendar-video-time">
                  {dayjs(getVideoDisplayDate(video)).format('h:mm A')}
                </Typography.Text>
                {video.publishedAccounts &&
                  video.publishedAccounts.length > 0 && (
                    <SocialIcon
                      provider={
                        video.publishedAccounts[0]
                          .socialPlatform as SocialProvider
                      }
                      style={{ fontSize: '14px' }}
                    />
                  )}
              </div>
              <Typography.Text
                ellipsis
                className="calendar-video-title-compact"
                title={video.title}
              >
                {video.title}
              </Typography.Text>
            </div>
          );
        })}
      </div>
    );
  };

  const monthCellRender = (value: Dayjs) => {
    const monthStart = value.startOf('month');
    const monthEnd = value.endOf('month');

    const monthVideos = videos.filter((video) => {
      const videoDate = dayjs(getVideoDisplayDate(video));
      return videoDate.isBetween(monthStart, monthEnd, 'day', '[]');
    });

    if (monthVideos.length === 0) return null;

    return (
      <div className="calendar-month-cell">
        <Badge
          count={monthVideos.length}
          style={{ backgroundColor: '#1890ff' }}
          overflowCount={999}
        />
      </div>
    );
  };

  const renderDailyView = () => {
    return <DailyTimelineView selectedDate={selectedDate} videos={videos} />;
  };

  const renderWeeklyView = () => {
    return <WeeklyTimelineView selectedDate={selectedDate} videos={videos} />;
  };

  const renderMonthlyView = () => {
    return (
      <div className="monthly-view">
        <ConfigProvider locale={antdLocale}>
          <Calendar
            value={selectedDate}
            onPanelChange={(date, mode) => {
              setSelectedDate(date);
              setCalendarMode(mode);
            }}
            cellRender={(current, info) => {
              if (info.type === 'date') {
                return dateCellRender(current);
              }
              if (info.type === 'month') {
                return monthCellRender(current);
              }
              return info.originNode;
            }}
          />
        </ConfigProvider>
      </div>
    );
  };

  const isLoading = loadingState === LoadingState.Loading;

  return (
    <>
      <Helmet>
        <title>Video Calendar | ShortsNinja</title>
        <meta
          name="description"
          content="View your video creation timeline in calendar view. Track your content creation by day, week, or month."
        />
      </Helmet>

      <div style={{ marginBottom: '20px' }}>
        <Typography.Paragraph type="secondary" style={{ marginBottom: '16px' }}>
          {t('videosCalendar.description')}
        </Typography.Paragraph>
        <Flex justify="flex-end" align="center" wrap="wrap" gap={12}>
          <Select
            value={viewMode}
            onChange={(value) => {
              setViewMode(value);
              if (value === 'monthly') {
                setCalendarMode('month');
              }
            }}
            style={{ width: 120 }}
            options={[
              { value: 'daily', label: t('videosCalendar.daily') },
              { value: 'weekly', label: t('videosCalendar.weekly') },
              { value: 'monthly', label: t('videosCalendar.monthly') },
            ]}
          />

          {viewMode !== 'monthly' && (
            <>
              <Button
                onClick={() =>
                  setSelectedDate(
                    selectedDate.subtract(
                      1,
                      viewMode === 'daily' ? 'day' : 'week'
                    )
                  )
                }
              >
                {t('videosCalendar.previous')}
              </Button>
              <Button type="primary" onClick={() => setSelectedDate(dayjs())}>
                {t('videosCalendar.today')}
              </Button>
              <Button
                onClick={() =>
                  setSelectedDate(
                    selectedDate.add(1, viewMode === 'daily' ? 'day' : 'week')
                  )
                }
              >
                {t('videosCalendar.next')}
              </Button>
            </>
          )}
        </Flex>
      </div>

      {isLoading ? (
        <Skeleton active />
      ) : (
        <>
          {viewMode === 'daily' && renderDailyView()}
          {viewMode === 'weekly' && renderWeeklyView()}
          {viewMode === 'monthly' && renderMonthlyView()}
        </>
      )}
    </>
  );
};

export default observer(VideosCalendar);
